import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Interfaces
import {
  ICostPerSessionRates,
  ICounsellor,
  ICounsellorDefaultRates,
  IFreeMinutes
} from '../../interfaces/counsellors.interfaces';

export interface ICurrentCounsellorState {
  counsellor: ICounsellor | null;
  session_rates: ICostPerSessionRates;
  free_minutes: IFreeMinutes;
  loading: boolean;
  error: string | null;
}

const initialState: ICurrentCounsellorState = {
  counsellor: null,
  session_rates: {
    available_video_call_duration: [],
    available_audio_call_duration: [],
    available_conversation_duration: [],
    audio_call_per_session_rate: [],
    conversation_per_session_rate: [],
    call_per_session_rate: [],
    worker_id: 0,
  },
  free_minutes: {
    free_minutes_enabled: false,
    full_free_service_enabled: false,
    prepaid_enabled: false,
    free_service_minutes: 0,
    free_buffer_minutes: 0,
    buffer_settings: [],
  },
  loading: false,
  error: null
};

export const currentCounsellorSlice = createSlice({
  name: 'current-counsellor',
  initialState,
  reducers: {
    profileRequest(state) {
      state.loading = true;
      state.error = null;
    },
    profileSuccess: (state, { payload }: PayloadAction<ICounsellor>) => {
      state.counsellor = payload;
      state.loading = false;
      state.error = null;
    },
    profileFailure: (state, { payload: error }: PayloadAction<string>) => {
      state.loading = false;
      state.error = error;
    },
    loadSessionRates: (state, { payload: { worker_session_rates, rates_enabled_count } }: PayloadAction<{
      worker_session_rates: ICostPerSessionRates,
      rates_enabled_count?: number
    }>) => {
      state.counsellor = state.counsellor ? { ...state.counsellor, ...(rates_enabled_count && { rates_enabled_count }) } : null;
      state.session_rates = worker_session_rates;
      state.loading = false;
      state.error = null;
    },
    loadFreeMinutes: (state, { payload: freeMinutes }: PayloadAction<IFreeMinutes>) => {
      state.free_minutes = freeMinutes;
    },
    updateFreeMinutes: (state, { payload: freeMinutes }: PayloadAction<IFreeMinutes>) => {
      state.free_minutes = { ...state.free_minutes, ...freeMinutes };
      state.loading = false;
      state.error = null;
    },
    profileUpdateRequest(state) {
      state.loading = true;
      state.error = null;
    },
    profileUpdateSuccess: (state, action: PayloadAction<ICounsellor>) => {
      state.counsellor = action.payload;
      state.loading = false;
      state.error = null;
    },
    profileUpdateFailure: (state, { payload: error }: PayloadAction<string>) => {
      state.loading = false;
      state.error = error;
    },
    updateStatusCounsellorByRates: (state, { payload: counsellor }: PayloadAction<ICounsellor>) => {
      if (state?.counsellor?.id !== counsellor.id) return;
      state.counsellor = counsellor;
    },
    updatePricePerMessage: (state, { payload: { worker_default_rates, rates_enabled_count } }: PayloadAction<{
      worker_default_rates: ICounsellorDefaultRates,
      rates_enabled_count?: number
    }>) => {
      state.counsellor = state.counsellor ? {
        ...state.counsellor, ...worker_default_rates,
        ...(typeof rates_enabled_count === 'number' && { rates_enabled_count })
      } : null;
      state.loading = false;
      state.error = null;
    },
    updateCurrentCounsellorInfo: (state, { payload }: PayloadAction<ICounsellor>) => {
      if (state.counsellor?.id !== payload.id) return;
      state.counsellor = { ...state.counsellor, ...payload };
    },
    profileDeleteRequest(state) {
      state.loading = true;
      state.error = null;
    },
    profileDeleteSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    profileDeleteFailure: (state, { payload: error }: PayloadAction<string>) => {
      state.loading = false;
      state.error = error;
    },

    updatePhotoRequest(state) {
      state.loading = true;
      state.error = null;
    },
    updatePhotoSuccess: (state, { payload }: PayloadAction<{ photo: string }>) => {
      state.counsellor = state?.counsellor ? { ...state.counsellor, photo: payload.photo } : null;
      state.loading = false;
      state.error = null;
    },
    updatePhotoFailure(state, { payload: error }: PayloadAction<string>) {
      state.loading = false;
      state.error = error;
    },


    deletePhotoRequest(state) {
      state.loading = true;
      state.error = null;
    },
    deletePhotoSuccess: (state) => {
      state.counsellor = state?.counsellor ? { ...state.counsellor, photo: null } : null;
      state.loading = false;
      state.error = null;
    },
    deletePhotoFailure(state, { payload: error }: PayloadAction<string>) {
      state.loading = false;
      state.error = error;
    },
  }
});

export const CurrentCounsellorSliceActions = currentCounsellorSlice.actions;
